import { useTranslation } from 'react-i18next';
import Image from 'next/image';

interface ChronosConfirmationBannerProps {
  /** Texto principal. Default: copy del banner de confirmación existente. */
  title?: string;
  /** Subtítulo. Default: copy del banner de confirmación existente. */
  subtitle?: string;
  /** Tracking link (íconos store). Default: Branch success-confirmation. */
  appLink?: string;
  /** QR a mostrar (debe codificar el mismo link). Default: QR de confirmación. */
  qrSrc?: string;
}

export function ChronosConfirmationBanner({
  title,
  subtitle,
  appLink = 'https://market.agendapro.com/app-ec-successconfirm',
  qrSrc = '/assets/qr-code-banner-confirmation.svg',
}: ChronosConfirmationBannerProps = {}) {
  const { t } = useTranslation();
  const APP_DOWNLOAD_LINK = appLink;
  const bannerTitle = title ?? t('CHRONOS_CONFIRMATION_BANNER.TITLE');
  const bannerSubtitle = subtitle ?? t('CHRONOS_CONFIRMATION_BANNER.SUBTITLE');

  return (
    <div className="w-full lg:w-[800px] md:mx-auto px-2 lg:px-0 pt-12 mb-7 relative">
      <a href={APP_DOWNLOAD_LINK} target="_blank" rel="noopener noreferrer" aria-label="Download the App">
        <div className="rounded-md overflow-hidden">
          <div className="w-full bg-backgroundChronos mt-auto mb-0">
            <div className="relative overflow-hidden px-6 md:px-12">
              <div className="pointer-events-none absolute md:-top-[200px] -top-[250px] md:left-[215px] left-[100px] z-0 w-[521px] h-[492px] md:h-[346px] md:w-[789px] bg-sunset-purple blur-[30px] [will-change:filter]" />
              <div className="pointer-events-none absolute md:-left-[120px] md:-bottom-[165px] z-0 h-[314px] md:h-[284px] w-[330px] md:w-[539px] -left-[100px] -bottom-[200px] bg-sunset-pink blur-[30px] [will-change:filter]" />
              <div className="relative flex flex-col md:flex-row justify-between z-1">
                <div className="flex basis-7/12 flex-col pt-7 pb-2 sm:py-5 text-left">
                  <p className="text-text-defaultInverse font-clash md:text-[22px] text-lg font-semibold md:pb-4 pb-2">
                    {bannerTitle}
                  </p>
                  <p className="text-text-defaultInverse font-poppins md:text-sm text-xs font-normal pb-4 pr-14 md:pr-28">
                    {bannerSubtitle}
                  </p>
                  <div className="w-[60px] h-[60px] hidden md:flex items-center justify-center bg-background-primary rounded-md">
                    <Image src={qrSrc} alt="qrApp" width={50} height={50} />
                  </div>
                </div>
                <div className="basis-5/12 flex md:items-end md:justify-center">
                  <div className="basis-1/3 md:hidden">
                    <div className="flex flex-col space-y-1">
                      <a
                        href={APP_DOWNLOAD_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Download on the App Store"
                      >
                        <Image src="/assets/AppStoreBadge.svg" alt="appStoreBadge" width={97} height={32} />
                      </a>
                      <a
                        href={APP_DOWNLOAD_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Get it on Google Play"
                      >
                        <Image src="/assets/GooglePlayBadge.png" alt="googlePlayBadge" width={97} height={32} />
                      </a>
                    </div>
                  </div>
                  <div className="basis-2/3 md:basis-full flex justify-end w-full">
                    <div className="flex justify-center max-w-[188px] md:max-w-[240px] w-full">
                      <img
                        src="/assets/confirmation-banner-phone.png"
                        alt="AgendaPro App preview"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </a>
    </div>
  );
}
